import React, { useState, useEffect, useRef } from 'react'
import './newsletter.scss'
import { useSelector } from 'react-redux'
import { RootState } from '../../store'
import { useQuery } from '@tanstack/react-query'
import { EngagementEvents, logCrmEvent } from '../../lib/utils/crmEvents'
import { newsletterServices } from '../../services/newsletter-service'
import { trackGaNewsletterSubscribe } from '../../lib/analytics/google-analytics'

export const NewsletterPage = () => {
  const {
    authToken,
    name,
    email: investorEmail,
  } = useSelector((state: RootState) => state.investor)

  const [nameInput, setNameInput] = useState(name ?? '')
  const [emailInput, setEmailInput] = useState(investorEmail ?? '')
  const [privacyChecked, setPrivacyChecked] = useState(false)
  const [marketingChecked, setMarketingChecked] = useState(false)
  const [submitState, setSubmitState] = useState<'idle' | 'submitting' | 'success' | 'error'>(
    'idle',
  )
  const hasLoggedVisitRef = useRef(false)

  const { data: isSubscribed, isFetching: isCheckingSubscription } = useQuery({
    queryKey: ['newsletter-subscriber-details', investorEmail],
    queryFn: async () => {
      if (investorEmail && investorEmail !== '') {
        const response = await newsletterServices.getNewsletterSubscriberDetails(investorEmail, {
          Authorization: authToken ?? '',
        })
        if (response.status === 200) {
          return true
        } else {
          return false
        }
      } else {
        return false
      }
    },
  })

  useEffect(() => {
    if (hasLoggedVisitRef.current) return
    hasLoggedVisitRef.current = true
    logCrmEvent(EngagementEvents.VISITED_NEWSLETTER, { Authorization: authToken ?? '' })
  }, [authToken])

  const isFormValid = nameInput.trim() !== '' && emailInput.trim() !== '' && privacyChecked

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!isFormValid) return

    setSubmitState('submitting')
    try {
      const response = await newsletterServices.subscribeToNewsletter(
        { name: nameInput, email: emailInput, marketingConsent: marketingChecked },
        { Authorization: authToken ?? '' },
      )
      if (response.status === 200) {
        trackGaNewsletterSubscribe()
        setSubmitState('success')
        setEmailInput('')
        setPrivacyChecked(false)
        setMarketingChecked(false)
      } else {
        setSubmitState('error')
      }
    } catch (error) {
      setSubmitState('error')
    }
  }

  return (
    <div className='nl-page'>
      {/* Hero + Subscribe section */}
      <section className='nl-hero'>
        <div className='nl-hero__inner'>
          <div className='nl-hero__content'>
            <p className='nl-hero__label'>Newsletter</p>
            <h1 className='nl-hero__title'>The Credibila Insider</h1>
            <p className='nl-hero__description'>
              Sign up to receive insights about private markets &amp; alternative investing in your
              inbox
            </p>
          </div>

          <div className='nl-hero__form-wrapper'>
            {isCheckingSubscription ? (
              <div className='nl-loader'>
                <div className='nl-loader__spinner'></div>
              </div>
            ) : submitState === 'success' || isSubscribed ? (
              <div className='nl-success'>
                <div className='nl-success__icon'>&#10003;</div>
                <h3 className='nl-success__title'>You&apos;re subscribed!</h3>
                <p className='nl-success__text'>
                  Thank you for subscribing to The Credibila Insider. You&apos;ll receive our next
                  newsletter in your inbox.
                </p>
              </div>
            ) : (
              <form className='nl-form' onSubmit={handleSubscribe}>
                {/* Name Input */}
                <div className='nl-form__input-group'>
                  <input
                    className='nl-form__input'
                    type='text'
                    placeholder='Name*'
                    value={nameInput}
                    disabled={authToken && name && name !== ''}
                    onChange={(e) => setNameInput(e.target.value.trim())}
                    required
                  />
                </div>

                {/* Email Input */}
                <div className='nl-form__input-group'>
                  <input
                    className='nl-form__input'
                    type='email'
                    placeholder='Email address*'
                    value={emailInput}
                    disabled={authToken && investorEmail && investorEmail !== ''}
                    onChange={(e) => setEmailInput(e.target.value.trim())}
                    required
                  />
                </div>

                <label className='nl-form__checkbox-label'>
                  <input
                    type='checkbox'
                    checked={privacyChecked}
                    onChange={(e) => setPrivacyChecked(e.target.checked)}
                    className='nl-form__checkbox'
                  />
                  <span className='nl-form__checkbox-text'>
                    I acknowledge Credibila&apos;s Privacy Policy and wish to receive
                    Credibila&apos;s newsletter. I can unsubscribe at any time.*
                  </span>
                </label>

                <label className='nl-form__checkbox-label'>
                  <input
                    type='checkbox'
                    checked={marketingChecked}
                    onChange={(e) => setMarketingChecked(e.target.checked)}
                    className='nl-form__checkbox'
                  />
                  <span className='nl-form__checkbox-text'>
                    I consent to Credibila using my personal data in direct marketing to keep me
                    posted about its products, services and events. I understand I can opt out at
                    any time.
                  </span>
                </label>

                <button
                  type='submit'
                  className='nl-form__submit'
                  disabled={!isFormValid || submitState === 'submitting'}
                >
                  {submitState === 'submitting' ? 'Subscribing...' : 'Subscribe'}
                </button>

                {submitState === 'error' && (
                  <p className='nl-form__error'>Something went wrong. Please try again.</p>
                )}
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  )
}
